import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { CheckCircle } from 'lucide-react';
import APP_ICONS, { PastelIcon } from '@/utils/appIcons';

/**
 * IntroScreen - Introduction to Emergency Binder
 * "If Something Happens to Me" purpose screen
 * Uses 3D pastel icons for consistency
 */

const IntroScreen = ({ onStart, completion, hasExistingProfiles }) => {
  const CHECKLIST = [
    { icon: APP_ICONS.medicalInfo, label: 'Medical Information' },
    { icon: APP_ICONS.familyContacts, label: 'Emergency Contacts' },
    { icon: APP_ICONS.documents, label: 'Important Documents' },
    { icon: APP_ICONS.pet, label: 'Household Information' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-red-50 to-white flex flex-col" data-testid="intro-screen">
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12">
        {/* Emergency Binder Icon */}
        <div className="w-24 h-24 rounded-2xl bg-red-50 flex items-center justify-center mb-6">
          <PastelIcon src={APP_ICONS.emergencyBinder} alt="Emergency Binder" size={64} />
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold text-slate-800 text-center mb-2">
          If Something Happens to Me
        </h1>

        {/* Description */}
        <p className="text-slate-600 text-center max-w-sm mb-8">
          All the important information doctors and family may need in an emergency — in one place.
        </p>

        {/* Checklist */}
        <Card className="w-full max-w-sm border-0 shadow-lg mb-8">
          <CardContent className="p-5">
            <div className="space-y-4">
              {CHECKLIST.map((item, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-slate-50 flex items-center justify-center">
                    <PastelIcon src={item.icon} alt={item.label} size={28} />
                  </div>
                  <span className="text-slate-700 font-medium">{item.label}</span>
                  <CheckCircle className="h-5 w-5 text-emerald-500 ml-auto" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* CTA Button */}
        <Button
          onClick={onStart}
          className="w-full max-w-sm h-12 bg-red-600 hover:bg-red-700 text-white font-medium text-base"
          data-testid="start-binder-btn"
        >
          {hasExistingProfiles ? 'Continue My Binder' : 'Start My Emergency Binder'}
        </Button>

        {/* Progress Bar (if existing) */}
        {hasExistingProfiles && completion > 0 && (
          <div className="w-full max-w-sm mt-4">
            <div className="flex items-center justify-between text-sm text-slate-600 mb-1">
              <span>Your progress</span>
              <span className="font-medium">{completion}% Complete</span>
            </div>
            <Progress value={completion} className="h-2" />
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="text-center pb-8 px-6">
        <p className="text-xs text-slate-400">
          Your information is stored securely and never shared without your permission.
        </p>
      </div>
    </div>
  );
};

export default IntroScreen;
